import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "../components/PublicLayout";
import { api } from "../lib/api";
import { useT } from "../i18n";
import {
  BookOpen, Headphones, Sprout, ShoppingBag, ArrowRight,
  Sparkles, Quote, Heart, Palette,
} from "lucide-react";

const FILES = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
// Route-locked Welcome hero. Never falls back to any global/pexels/unsplash image.
// Uses REACT_APP_BACKEND_URL so preview & production both resolve correctly.
const WELCOME_HERO_IMG = `${FILES}bala-basava/hero/welcome/c768bd7ac92b4ae7a6947766c7ed4f93.png`;
// Route-locked Early-Learning image ("Why Children Must Learn Vachanas Early").
const EARLY_LEARNING_IMG_FIXED = `${FILES}bala-basava/children/welcome/b1c2a3d4e5f60718293a4b5c6d7e8f90.png`;

const COMICS = [
  {
    id: "vol1",
    title: "Bala Basava Volume 1 – Foundation of Values",
    description: "Stories that introduce children to equality, kindness, and truth.",
    image: "https://d1n4jo9i5084wt.cloudfront.net/cover/1410179923/695419252f61b-9451895370.png",
    amazon: "https://www.amazon.in/dp/B0GDQPRMCM",
    flipkart: "https://www.flipkart.com/bala-basava/p/itm52c2bd30ae16a",
    notion: "https://direct.notionpress.com/in/read/bala-basava",
  },
  {
    id: "vol2",
    title: "Bala Basava Volume 2 – Thinking & Responsibility",
    description: "Helps children think clearly and make better decisions.",
    image: "https://d1n4jo9i5084wt.cloudfront.net/cover/1410184015/69b7dcf271baa-5874700075.png",
    amazon: "https://www.amazon.in/dp/B0GSRPQRWB",
    flipkart: "https://www.flipkart.com/bala-basava-volume-2/p/itm1328b6bb6bc1b",
    notion: "https://direct.notionpress.com/in/read/bala-basava-volume-2-hardcover",
  },
  {
    id: "vol3",
    title: "Bala Basava Volume 3 – Courage & Inner Strength",
    description: "Stories that build courage and confidence.",
    image: "https://d1n4jo9i5084wt.cloudfront.net/cover/1410186183/69cf55756a196-9880452486.png",
    amazon: "https://www.amazon.in/dp/B0GVYYWHLT",
    flipkart: "https://www.flipkart.com/bala-basava-volume-3/p/itm6692a49c6ae59",
    notion: "https://direct.notionpress.com/in/read/bala-basava-volume-3/paperback",
  },
  {
    id: "vol4",
    title: "Bala Basava Volume 4 – Discipline & Action",
    description: "Teaches discipline, effort, and perseverance.",
    image: "https://d1n4jo9i5084wt.cloudfront.net/cover/1410186184/69cf9283af590-3614894342.png",
    amazon: "https://www.amazon.in/dp/B0GVZ5RHB8",
    flipkart: "https://www.flipkart.com/bala-basava-volume-4/p/itm54cf6a9556c3e",
    notion: "https://direct.notionpress.com/in/read/bala-basava-volume-4/paperback",
  },
  {
    id: "vol5",
    title: "Bala Basava Volume 5 – Reflection & Wisdom",
    description: "Encourages self-awareness and deeper thinking.",
    image: "https://d1n4jo9i5084wt.cloudfront.net/cover/1410186781/69d8749076b36-7898725531.png",
    amazon: "https://www.amazon.in/dp/B0GWQPGB15",
    flipkart: "https://www.flipkart.com/bala-basava-volume-5/p/itm4e04873d621f3",
    notion: "https://direct.notionpress.com/in/read/bala-basava-volume-5",
  },
];

export default function Welcome() {
  const { t } = useT();
  const [customImgs, setCustomImgs] = useState({});
  const [heroBroken, setHeroBroken] = useState(false);
  useEffect(() => {
    api.get("/public/welcome-images").then(r => setCustomImgs(r.data || {})).catch(() => {});
  }, []);
  // Welcome hero is ALWAYS the fixed route-locked image — never inherits
  // any global/pexels/unsplash fallback. If the fixed URL fails to load we
  // show a neutral branded background instead.
  const HERO_IMG = WELCOME_HERO_IMG;
  // Basavanna / Early-learning slots stay admin-configurable, but no longer
  // fall back to the pexels catalogue — a neutral CSS panel is used when
  // no admin upload exists so the Welcome page never renders stock imagery.
  const BASAVANNA_IMG = customImgs.basavanna ? FILES + customImgs.basavanna : null;
  // Early-learning is pinned to the route-locked Bala Basava image; only if
  // an admin has explicitly uploaded a replacement to the "early" slot do we
  // honour that. No stock-photo fallback.
  const EARLY_LEARNING_IMG = customImgs.early ? FILES + customImgs.early : EARLY_LEARNING_IMG_FIXED;

  const scrollToComics = (e) => {
    e?.preventDefault?.();
    document.getElementById("comics")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <PublicLayout>
      {/* ============== A) HERO ============== */}
      <section className="relative overflow-hidden" data-testid="welcome-page">
        <div className="absolute inset-0 -z-10 bg-[var(--bb-paper)]" />
        <div className="absolute -top-32 -left-24 w-[420px] h-[420px] rounded-full bg-[var(--bb-terracotta)]/10 blur-3xl pointer-events-none -z-10" />
        <div className="absolute top-40 -right-24 w-[380px] h-[380px] rounded-full bg-[var(--bb-forest)]/10 blur-3xl pointer-events-none -z-10" />

        <div className="max-w-6xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-16 md:pb-24 grid md:grid-cols-12 gap-10 md:gap-14 items-center">
          <div className="md:col-span-7">
            <div className="bb-overline text-[var(--bb-terracotta)] mb-5 flex items-center gap-2">
              <Sparkles size={12}/> {t("w_overline")}
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl text-[var(--bb-forest)] tracking-tight leading-[1.02] max-w-4xl" data-testid="welcome-title">
              {t("w_title_1")}<br/>{t("w_title_2")}
            </h1>
            <p className="mt-7 font-serif text-2xl sm:text-3xl text-[var(--bb-forest)]/90 italic max-w-3xl leading-snug">
              {t("w_tagline")}
            </p>
            <div className="mt-8 max-w-2xl text-[var(--bb-ink)]/80 leading-relaxed text-base sm:text-lg space-y-4">
              <p>{t("w_intro_1")}</p>
              <p>
                {t("w_intro_2a")}<br/>
                <span className="text-[var(--bb-forest)] font-medium">{t("w_intro_2b")}</span>
              </p>
            </div>

            <div className="mt-12 max-w-2xl border-l-4 border-[var(--bb-terracotta)] pl-5 sm:pl-6 py-2">
              <p className="font-serif text-xl sm:text-2xl text-[var(--bb-forest)] leading-snug">
                {t("w_identity_1")}<br/>
                {t("w_identity_2")}
              </p>
            </div>

            <div className="mt-6 max-w-2xl">
              <p className="text-[var(--bb-ink)]/80 text-base sm:text-lg leading-relaxed">
                <span className="text-[var(--bb-terracotta)] font-medium">{t("w_fomo_highlight")}</span> {t("w_fomo_rest")}
              </p>
            </div>

            <div className="mt-9 flex flex-wrap items-center gap-3">
              <a href="#comics" onClick={scrollToComics} className="bb-btn-primary inline-flex items-center gap-2" data-testid="cta-explore-comics">
                <BookOpen size={15}/> {t("w_cta_explore_comics")} <ArrowRight size={14}/>
              </a>
              <Link to="/audio-comics" className="bb-btn-secondary inline-flex items-center gap-2" data-testid="cta-audio-experience">
                <Headphones size={15}/> {t("w_cta_start_audio")}
              </Link>
              <Link to="/ecomics" className="bb-btn-secondary inline-flex items-center gap-2" data-testid="cta-ecomics-hero">
                <Palette size={15}/> Explore E-Comics
              </Link>
            </div>

            <div className="mt-10 flex items-center gap-3 text-[13px] text-[var(--bb-muted)]">
              <Quote size={14} className="text-[var(--bb-terracotta)]"/>
              <span>{t("w_trust_line")}</span>
            </div>
          </div>

          <div className="md:col-span-5 relative" data-testid="welcome-hero-image">
            <div className="aspect-[4/5] rounded-md overflow-hidden border border-[var(--bb-border)] shadow-[0_30px_60px_-30px_rgba(44,53,49,0.4)] bg-gradient-to-br from-[var(--bb-sand)] via-[var(--bb-paper)] to-[var(--bb-sand)]">
              {!heroBroken && (
                <img
                  src={HERO_IMG}
                  alt=""
                  className="w-full h-full object-cover"
                  loading="eager"
                  onError={() => setHeroBroken(true)}
                  data-testid="welcome-hero-img"
                />
              )}
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-black/0 to-transparent" />
            </div>
          </div>
        </div>
      </section>

      {/* ============== B) INTRO ============== */}
      <section className="border-t border-[var(--bb-border)] bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-20">
          <div className="space-y-5 text-lg sm:text-xl text-[var(--bb-ink)]/85 leading-relaxed font-light">
            <p>{t("w_intro_body_1")}</p>
            <p>
              {t("w_intro_body_2a")} <span className="text-[var(--bb-forest)] font-medium font-normal">{t("w_intro_body_2b")}</span>
            </p>
          </div>
        </div>
      </section>

      {/* ============== C) THREE PATHWAYS ============== */}
      <section className="bg-[var(--bb-sand)]/40">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="grid md:grid-cols-3 gap-5 md:gap-7">
            <PathwayCard icon={<BookOpen size={20}/>} tag={t("w_path_comics_tag")} title={t("w_path_comics_title")} description={t("w_path_comics_body")} ctaLabel={t("w_path_comics_cta")} onClick={scrollToComics} testid="pathway-comics"/>
            <PathwayCard icon={<Headphones size={20}/>} tag={t("w_path_audio_tag")} title={t("w_path_audio_title")} description={t("w_path_audio_body")} ctaLabel={t("w_path_audio_cta")} href="/audio-comics" testid="pathway-audio"/>
            <PathwayCard icon={<Sprout size={20}/>} tag={t("w_path_growth_tag")} title={t("w_path_growth_title")} description={t("w_path_growth_body")} ctaLabel={t("w_path_growth_cta")} href="/home" testid="pathway-growth"/>
          </div>
        </div>
      </section>

      {/* ============== D) BASAVANNA ============== */}
      <section className="bg-white border-t border-[var(--bb-border)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
            <div className="md:col-span-5 md:sticky md:top-24" data-testid="basavanna-image">
              <div className="aspect-[4/5] rounded-md overflow-hidden border border-[var(--bb-border)] shadow-[0_30px_60px_-30px_rgba(44,53,49,0.35)] relative bg-gradient-to-br from-[var(--bb-sand)] via-[var(--bb-paper)] to-[var(--bb-sand)]">
                {BASAVANNA_IMG && (
                  <img src={BASAVANNA_IMG} alt="" className="w-full h-full object-cover" loading="lazy"/>
                )}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[var(--bb-forest)]/35 via-transparent to-transparent"/>
              </div>
              <p className="mt-3 text-[11px] uppercase tracking-widest text-[var(--bb-muted)] flex items-center gap-2">
                <Sparkles size={10} className="text-[var(--bb-terracotta)]"/> {t("w_basavanna_image_caption")}
              </p>
            </div>

            <div className="md:col-span-7">
              <div className="bb-overline text-[var(--bb-terracotta)] mb-4">{t("w_basavanna_overline")}</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-[var(--bb-forest)] tracking-tight leading-tight">
                {t("w_basavanna_title")}
              </h2>
              <div className="mt-8 space-y-5 text-lg text-[var(--bb-ink)]/85 leading-relaxed">
                <p>
                  {t("w_basavanna_p1a")} <span className="text-[var(--bb-forest)] font-medium">{t("w_basavanna_p1b")}</span>
                </p>
                <p>
                  {t("w_basavanna_p2a")} <span className="font-serif italic text-[var(--bb-forest)]">{t("w_basavanna_p2b")}</span>{t("w_basavanna_p2c")}
                </p>
              </div>

              <div className="mt-10 bb-card p-7 sm:p-8 bg-gradient-to-br from-[var(--bb-sand)]/60 to-white">
                <div className="bb-overline text-[var(--bb-terracotta)] mb-2">{t("w_he_introduced")}</div>
                <p className="font-serif text-3xl sm:text-4xl text-[var(--bb-forest)] italic leading-snug">
                  "{t("w_kayakave")}"
                </p>
                <p className="text-base text-[var(--bb-ink)]/75 mt-2">{t("w_kayakave_sub")}</p>
                <ul className="mt-6 space-y-2 text-[var(--bb-ink)]/85">
                  {[t("w_benefit_1"), t("w_benefit_2"), t("w_benefit_3"), t("w_benefit_4")].map((x) => (
                    <li key={x} className="flex items-center gap-3">
                      <span className="w-1.5 h-1.5 rounded-full bg-[var(--bb-terracotta)] shrink-0"/>
                      {x}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-10 space-y-5 text-lg text-[var(--bb-ink)]/85 leading-relaxed">
                <p>{t("w_never_stay")}</p>
                <p className="text-[var(--bb-forest)] font-medium font-serif text-xl sm:text-2xl">
                  {t("w_meant_to_shape")}
                </p>
              </div>

              <div className="mt-14 border-l-4 border-[var(--bb-terracotta)] pl-6 py-3 max-w-2xl">
                <p className="text-[var(--bb-muted)] text-sm uppercase tracking-widest mb-2">{t("w_question_label")}</p>
                <p className="font-serif text-2xl sm:text-3xl text-[var(--bb-forest)] leading-snug">
                  {t("w_question_1")}<br/>
                  {t("w_question_2")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== E) WHY VACHANAS ============== */}
      <section className="bg-[var(--bb-sand)]/40 border-t border-[var(--bb-border)]">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="grid md:grid-cols-12 gap-10 md:gap-14 items-start">
            <div className="md:col-span-7">
              <div className="bb-overline text-[var(--bb-terracotta)] mb-4">{t("w_early_overline")}</div>
              <h2 className="font-serif text-4xl sm:text-5xl text-[var(--bb-forest)] tracking-tight leading-tight">
                {t("w_early_title")}
              </h2>
              <div className="mt-9 space-y-5 text-lg text-[var(--bb-ink)]/85 leading-relaxed">
                <p>
                  {t("w_early_absorb_1")}<br/>
                  <span className="text-[var(--bb-forest)] font-medium">{t("w_early_absorb_2")}</span>
                </p>
              </div>
            </div>

            <div className="md:col-span-5 md:sticky md:top-24" data-testid="early-learning-image">
              <div className="aspect-[4/5] rounded-md overflow-hidden border border-[var(--bb-border)] shadow-[0_30px_60px_-30px_rgba(44,53,49,0.35)] relative bg-gradient-to-br from-[var(--bb-sand)] via-[var(--bb-paper)] to-[var(--bb-sand)]">
                {EARLY_LEARNING_IMG && (
                  <img
                    src={EARLY_LEARNING_IMG}
                    alt=""
                    className="w-full h-full object-cover"
                    loading="lazy"
                    data-testid="early-learning-img"
                  />
                )}
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/30 via-transparent to-transparent"/>
              </div>
              <p className="mt-3 text-[11px] uppercase tracking-widest text-[var(--bb-muted)] flex items-center gap-2">
                <Sparkles size={10} className="text-[var(--bb-terracotta)]"/> {t("w_early_image_caption")}
              </p>
            </div>
          </div>

          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className="bb-card p-7 bg-white">
              <div className="bb-overline text-[var(--bb-forest)] mb-3">{t("w_if_reach_early")}</div>
              <ul className="space-y-2.5 text-[var(--bb-ink)]/85">
                {[t("w_reach_1"), t("w_reach_2"), t("w_reach_3"), t("w_reach_4")].map((x) => (
                  <li key={x} className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--bb-forest)] shrink-0"/>
                    {x}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bb-card p-7 bg-white border-l-4 border-l-[var(--bb-terracotta)]">
              <div className="bb-overline text-[var(--bb-terracotta)] mb-3">{t("w_if_they_dont")}</div>
              <ul className="space-y-2.5 text-[var(--bb-ink)]/85">
                {[t("w_dont_1"), t("w_dont_2")].map((x) => (
                  <li key={x} className="flex items-start gap-3">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[var(--bb-terracotta)] shrink-0"/>
                    {x}
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="font-serif text-3xl sm:text-4xl text-[var(--bb-forest)] italic max-w-2xl mx-auto leading-snug whitespace-pre-line">
              {t("w_fofo")}
            </p>
          </div>

          <div className="mt-14 max-w-2xl mx-auto text-center space-y-4 text-lg text-[var(--bb-ink)]/85 leading-relaxed">
            <p>{t("w_petro_intro")}</p>
            <div className="grid grid-cols-2 gap-4 mt-6 max-w-md mx-auto">
              <div className="p-5 rounded-md border border-[var(--bb-border)] bg-white">
                <div className="text-sm text-[var(--bb-muted)] mb-1">{t("w_resources_label")}</div>
                <div className="font-serif text-lg text-[var(--bb-forest)]">{t("w_resources_body")}</div>
              </div>
              <div className="p-5 rounded-md border border-[var(--bb-terracotta)]/30 bg-[var(--bb-terracotta)]/5">
                <div className="text-sm text-[var(--bb-terracotta)] mb-1">{t("w_values_label")}</div>
                <div className="font-serif text-lg text-[var(--bb-forest)]">{t("w_values_body")}</div>
              </div>
            </div>
          </div>

          <div className="mt-16 max-w-3xl mx-auto">
            <p className="text-[var(--bb-ink)]/85 text-lg mb-5">{t("w_children_learn")}</p>
            <div className="grid sm:grid-cols-3 gap-4">
              {[
                [t("w_learn_a1"), t("w_learn_b1")],
                [t("w_learn_a2"), t("w_learn_b2")],
                [t("w_learn_a3"), t("w_learn_b3")],
              ].map(([a, b]) => (
                <div key={a} className="bb-card p-5 text-center bg-white">
                  <div className="font-serif text-2xl text-[var(--bb-forest)]">{a}</div>
                  <div className="text-sm text-[var(--bb-muted)] mt-1">{b}</div>
                </div>
              ))}
            </div>
            <p className="mt-8 text-center text-[var(--bb-ink)]/85 text-lg leading-relaxed">
              {t("w_neuro_close_1")} <span className="text-[var(--bb-forest)] font-medium">{t("w_neuro_close_2")}</span> {t("w_neuro_close_3")}
            </p>
          </div>
        </div>
      </section>

      {/* ============== F) COMICS ============== */}
      <section id="comics" className="bg-white border-t border-[var(--bb-border)] scroll-mt-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <div className="bb-overline text-[var(--bb-terracotta)] mb-3">{t("w_comics_overline")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[var(--bb-forest)] tracking-tight leading-tight">
              {t("w_comics_title")}
            </h2>
            <p className="mt-4 text-[var(--bb-muted)]">{t("w_comics_sub")}</p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
            {COMICS.map((c) => (<ComicCard key={c.id} comic={c}/>))}
          </div>

          <p className="mt-12 text-center text-sm text-[var(--bb-muted)]">{t("w_comics_trust")}</p>
        </div>
      </section>

      {/* ============== G) AUDIO ============== */}
      <section className="bg-gradient-to-br from-[#0e0a14] to-[#1a0e2c] text-white">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-24 text-center">
          <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-amber-300 mb-5">
            <Headphones size={12}/> {t("w_audio_overline")}
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-tight">
            {t("w_audio_title")}
          </h2>
          <p className="mt-6 text-white/75 text-lg max-w-xl mx-auto leading-relaxed">
            {t("w_audio_body")}
          </p>
          <Link to="/audio-comics" className="mt-9 inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-amber-400 text-black font-semibold hover:bg-amber-300 transition" data-testid="cta-audio-bottom">
            <Headphones size={16}/> {t("w_cta_start_audio")} <ArrowRight size={14}/>
          </Link>
        </div>
      </section>

      {/* ============== G2) E-COMICS ============== */}
      <section className="bg-white border-t border-[var(--bb-border)]">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-14 md:py-18">
          <div className="bb-card p-8 md:p-10 bg-[var(--bb-sand)]/40 text-center" data-testid="ecomics-welcome-card">
            <div className="w-11 h-11 rounded-full bg-[var(--bb-terracotta)]/10 text-[var(--bb-terracotta)] grid place-items-center mx-auto mb-4">
              <Palette size={18}/>
            </div>
            <div className="bb-overline text-[var(--bb-terracotta)] mb-2">E-COMICS</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-[var(--bb-forest)] tracking-tight">
              Read, listen, explore and colour Bala Basava stories.
            </h2>
            <Link to="/ecomics" className="mt-7 bb-btn-primary inline-flex items-center gap-2" data-testid="cta-ecomics-card">
              <Palette size={15}/> Explore E-Comics <ArrowRight size={14}/>
            </Link>
          </div>
        </div>
      </section>

      {/* ============== H) FINAL ============== */}
      <section className="bg-[var(--bb-sand)]/50 border-t border-[var(--bb-border)]">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="text-center mb-12">
            <div className="bb-overline text-[var(--bb-terracotta)] mb-3">{t("w_final_overline")}</div>
            <h2 className="font-serif text-4xl sm:text-5xl text-[var(--bb-forest)] tracking-tight leading-tight">
              {t("w_final_title")}
            </h2>
          </div>
          <div className="grid sm:grid-cols-3 gap-5">
            {[
              { icon: <BookOpen size={18}/>, title: t("w_final_learn"), body: t("w_final_learn_sub") },
              { icon: <Headphones size={18}/>, title: t("w_final_experience"), body: t("w_final_experience_sub") },
              { icon: <Heart size={18}/>, title: t("w_final_grow"), body: t("w_final_grow_sub") },
            ].map((x) => (
              <div key={x.title} className="bb-card p-7 text-center bg-white">
                <div className="w-11 h-11 rounded-full bg-[var(--bb-terracotta)]/10 text-[var(--bb-terracotta)] grid place-items-center mx-auto mb-4">
                  {x.icon}
                </div>
                <div className="font-serif text-2xl text-[var(--bb-forest)]">{x.title}</div>
                <div className="text-sm text-[var(--bb-muted)] mt-1">{x.body}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </PublicLayout>
  );
}

function PathwayCard({ icon, tag, title, description, ctaLabel, href, onClick, testid }) {
  const inner = (
    <div className="bb-card p-7 h-full bg-white hover:shadow-[0_20px_40px_-20px_rgba(44,53,49,0.25)] transition group">
      <div className="w-11 h-11 rounded-full bg-[var(--bb-terracotta)]/10 text-[var(--bb-terracotta)] grid place-items-center mb-5">
        {icon}
      </div>
      <div className="bb-overline text-[var(--bb-muted)] mb-2">{tag}</div>
      <h3 className="font-serif text-2xl text-[var(--bb-forest)] leading-snug mb-3">{title}</h3>
      <p className="text-[var(--bb-ink)]/75 text-sm leading-relaxed">{description}</p>
      <div className="mt-6 inline-flex items-center gap-1 text-sm text-[var(--bb-terracotta)] font-medium group-hover:gap-2 transition-all">
        {ctaLabel} <ArrowRight size={14}/>
      </div>
    </div>
  );
  if (onClick) return <button onClick={onClick} className="text-left" data-testid={testid}>{inner}</button>;
  if (href?.startsWith("/")) return <Link to={href} data-testid={testid}>{inner}</Link>;
  return <a href={href} target="_blank" rel="noreferrer" data-testid={testid}>{inner}</a>;
}

function ComicCard({ comic }) {
  return (
    <div className="bb-card p-5 flex flex-col bg-white" data-testid={`comic-${comic.id}`}>
      <div className="aspect-[3/4] rounded overflow-hidden bg-[var(--bb-sand)] mb-5 ring-1 ring-[var(--bb-border)]">
        <img src={comic.image} alt={comic.title} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.opacity = 0.4; }}/>
      </div>
      <h3 className="font-serif text-xl text-[var(--bb-forest)] leading-snug mb-2">{comic.title}</h3>
      <p className="text-sm text-[var(--bb-ink)]/70 leading-relaxed flex-1">{comic.description}</p>
      <div className="mt-5 grid grid-cols-3 gap-2">
        <BuyBtn href={comic.amazon} label="Amazon" testid={`buy-amazon-${comic.id}`}/>
        <BuyBtn href={comic.flipkart} label="Flipkart" testid={`buy-flipkart-${comic.id}`}/>
        <BuyBtn href={comic.notion} label="Notion" testid={`buy-notion-${comic.id}`}/>
      </div>
    </div>
  );
}

function BuyBtn({ href, label, testid }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-1.5 text-xs font-medium px-2.5 py-2.5 rounded-md border border-[var(--bb-border)] text-[var(--bb-forest)] bg-white hover:bg-[var(--bb-forest)] hover:text-white hover:border-[var(--bb-forest)] transition" data-testid={testid}>
      <ShoppingBag size={11}/> {label}
    </a>
  );
}
